import { OrganizationKind, type SessionEmployment } from "@digits-graphql/frontend/graphql-public"
import objectHelper from "@digits-shared/helpers/objectHelper"
import type { JWTAffiliation, JWTLegalEntity } from "@digits-shared/session/jwt/jwtLegalEntity"
import type { JWTEmployment } from "@digits-shared/session/jwt/jwtUser"
import { SessionPermissions } from "@digits-shared/session/Permissions"
import SessionAffiliation from "src/frontend/session/Affiliation"
import SessionAffiliationOrganization from "src/frontend/session/AffiliationOrganization"
import SessionBusinessOrganization, {
  type SessionBusinessOrganizationAttributes,
} from "src/frontend/session/BusinessOrganization"
import type FrontendSession from "src/frontend/session/FrontendSession"
import SessionLegalEntity from "src/frontend/session/LegalEntity"
import type { FrontendPermissionModule } from "src/frontend/session/permissionModule"
import { Experience } from "src/frontend/session/personas"
import AssetsHelper, { CDNAsset } from "src/shared/helpers/assetsHelper"

/**
 * Used when parsing a JWT session. Decodes a JWTEmployment
 * into a SessionOrganization and stores it on a passed array of
 * SessionOrganizations.
 */
export function decodeJWTEmployment(
  organizations: SessionBusinessOrganization[],
  employment: JWTEmployment
) {
  const { id: employmentId, org, perms, a: employmentActivated, priexp } = employment

  const { id, name, slug, icon, entities, affiliations: rawAffiliations } = org
  const legalEntities = entities
    ? entities.map<SessionLegalEntity>((e) => decodeJWTLegalEntity(id, e))
    : []

  const affiliations = rawAffiliations
    ? rawAffiliations.map<SessionAffiliation>((af) => decodeJWTAffiliation(id, af))
    : undefined

  const permissions = new SessionPermissions(perms)
  const priExpKey = objectHelper.keysOf(Experience).find((k) => Experience[k] === priexp)
  const primaryExperience = priExpKey ? Experience[priExpKey] : undefined

  const organization: SessionBusinessOrganizationAttributes = {
    id,
    name,
    slug,
    iconUrl: AssetsHelper.publicUrl(CDNAsset.OrganizationIcons, icon),
    legalEntities,
    affiliations,
    permissions,
    employmentId,
    employmentActivated,
    primaryExperience,
    kind: org.kind,
  }
  organizations.push(new SessionBusinessOrganization(organization))

  return organizations
}

export function decodeSessionEmployment(
  session: FrontendSession,
  organizations: SessionBusinessOrganization[],
  employment: SessionEmployment
) {
  const { id: employmentId, organization, affiliations: rawAffiliations } = employment
  const { id, name, slug, iconUrl, legalEntities: entities, kind } = organization

  const affiliations = rawAffiliations
    ? rawAffiliations.flatMap<SessionAffiliation>((af) =>
        af.organization.legalEntities.map((entity) => {
          // Find existing affiliation in session organizations to copy permissions
          const existingOrg = session.findOrganizationByLegalEntityId(entity.id)
          const existingPerms =
            existingOrg?.affiliations?.find(
              (existing) =>
                existing.entity.id === entity.id && existing.organization.id === af.organization.id
            )?.permissions?.rawPermissions ?? {}

          const raw: JWTAffiliation = {
            id: af.id,
            org: { ...af.organization, icon: af.organization.iconUrl ?? undefined },
            entity,
            perms: { ...existingPerms },
          }
          return decodeJWTAffiliation(id, raw)
        })
      )
    : undefined

  const legalEntities = entities
    ? entities.map<SessionLegalEntity>((e) => decodeJWTLegalEntity(id, e))
    : []

  // Copy existing permissions if organization exists in session
  const existingOrg = session.findOrganizationById(id)
  const permissions = new SessionPermissions(existingOrg?.permissions?.rawPermissions ?? {})

  const orgAttributes: SessionBusinessOrganizationAttributes = {
    id,
    name,
    slug,
    iconUrl: iconUrl ? AssetsHelper.publicUrl(CDNAsset.OrganizationIcons, iconUrl) : undefined,
    legalEntities,
    affiliations,
    employmentId,
    permissions,
    kind: kind || OrganizationKind.UnknownKind,
  }
  organizations.push(new SessionBusinessOrganization(orgAttributes))
  return organizations
}

export function decodeJWTLegalEntity(
  organizationId: string,
  rawLegalEntity: JWTLegalEntity,
  affiliationId?: string
) {
  return new SessionLegalEntity(organizationId, rawLegalEntity, affiliationId)
}

export function decodeJWTAffiliation(organizationId: string, rawAffiliation: JWTAffiliation) {
  const {
    entity: rawEntity,
    org: rawOrganization,
    perms,
    priexp,
    ...rawAttributes
  } = rawAffiliation

  const entity = decodeJWTLegalEntity(organizationId, rawEntity, rawAttributes.id)

  const organization = new SessionAffiliationOrganization({
    ...(rawOrganization || {}),
    iconUrl: rawOrganization.icon
      ? AssetsHelper.publicUrl(CDNAsset.OrganizationIcons, rawOrganization.icon)
      : undefined,
  })

  const permissions = new SessionPermissions<FrontendPermissionModule>(perms)

  const priExpKey = objectHelper.keysOf(Experience).find((k) => Experience[k] === priexp)
  const primaryExperience = priExpKey ? Experience[priExpKey] : undefined

  const attributes = {
    ...rawAttributes,
    entity,
    organization,
    permissions,
    primaryExperience,
  }
  return new SessionAffiliation(attributes)
}
